import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Label } from '../../../../../components/ui/label';
import { Input } from '../../../../../components/ui/input';
import { Hash, FileText, Download, Trash2, Upload, Loader2, RefreshCw, AlertCircle, MoreHorizontal, Pencil } from 'lucide-react';
import { DeleteConfirmationDialog } from '../../../../../components/ui/DeleteConfirmationDialog';
import { DocumentUpload } from '../specifications/DocumentUpload';
import { PdfViewer } from '../../../../../components/viewers/PdfViewer';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../../../../components/ui/custom-dropdown-menu';
import { CollapsibleFilterBar } from '../materials/tabs/Filters/CollapsibleFilterBar';
import { MultiSelect } from '../../../../../components/ui/multi-select';
import {
  fetchARKRegistryDocs,
  deleteARKRegistryDoc,
  clearARKRegistryDocs,
  uploadARKRegistryDoc,
  setARKRegistryDocSuccess
} from '../../../../../redux/ProductionData/actions';
import axiosConfig from '../../../../../redux/ProductionData/axiosConfig';
import { Notification } from '../../../../../components/ui/custom-notification';
import { SearchableSelect } from '../../../../../components/ui/SearchableSelect';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import { AddMaterialCategory, getListMaterialCategory } from '../../../../../redux/Project/materialSaga';

interface ARKRegistryDocumentTableProps {
  projectId: number;
  projectName?: string;
  canEdit?: boolean;
}

export const ARKRegistryDocumentTable: React.FC<ARKRegistryDocumentTableProps> = ({
  projectId,
  projectName,
  canEdit = true,
}) => {
  const dispatch = useDispatch();
  
  // Redux state
  const {
    arkRegistryDocs,
    arkRegistryDocsLoading,
    arkRegistryDocsError,
    uploadARKRegistryDocLoading,
    uploadARKRegistryDocSuccess,
    deleteARKRegistryDocLoading,
    deleteARKRegistryDocSuccess
  } = useSelector((state: any) => state.productionData || {});

  // Local state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  
  // PDF Viewer state
  const [viewerFile, setViewerFile] = useState<{ fileUrl: string; fileName: string } | null>(null);

  // Fetch documents on mount
  useEffect(() => {
    if (projectId) {
      dispatch(fetchARKRegistryDocs({ projectId }));
    }
    
    return () => {
      dispatch(clearARKRegistryDocs());
    };
  }, [projectId, dispatch]);

  // Refresh after successful upload
  useEffect(() => {
    if (uploadARKRegistryDocSuccess && projectId) {
      dispatch(fetchARKRegistryDocs({ projectId }));
      setIsUploadModalOpen(false);
      setSelectedFiles([]);
      setSelectedTags({});
      // Clear success flag
      dispatch(setARKRegistryDocSuccess(null));
    }
  }, [uploadARKRegistryDocSuccess, projectId, dispatch]);

  // Refresh after successful delete
  useEffect(() => {
    if (deleteARKRegistryDocSuccess && projectId) {
      // Force refresh after a short delay
      setTimeout(() => {
      dispatch(fetchARKRegistryDocs({ projectId }));
      }, 300);
      setIsDeleteModalOpen(false);
      setDocumentToDelete(null);
    }
  }, [deleteARKRegistryDocSuccess, projectId, dispatch]);

  // Manual refresh function
  const handleRefresh = useCallback(() => {
    if (projectId) {
      dispatch(fetchARKRegistryDocs({ projectId }));
    }
  }, [projectId, dispatch]);

  // Handle document download/view
  const handleDownloadDocument = useCallback((doc: any) => {
    if (!doc || !doc.fileUrl) {
      return;
    }
    setViewerFile({
      fileUrl:  doc.fileUrl,
      fileName: doc.fileName || 'Document'
    });
  }, []);

  // Close viewer
  const closeViewer = () => {
    setViewerFile(null);
  };

  // Handle delete click
  const handleDeleteClick = useCallback((doc: any) => {
    setDocumentToDelete(doc);
    setIsDeleteModalOpen(true);
  }, []);

  // Confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (!documentToDelete) return;
    
    dispatch(deleteARKRegistryDoc({
      docIds: [documentToDelete.docId],
      projectId
    }));
  }, [documentToDelete, projectId, dispatch]);

  // Cancel delete
  const handleCancelDelete = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDocumentToDelete(null);
  }, []);

  // Edit document type, document number, and notes
  const [isEditTypeModalOpen, setIsEditTypeModalOpen] = useState(false);
  const [documentToEdit, setDocumentToEdit] = useState<any>(null);
  const [editDocumentType, setEditDocumentType] = useState<string>('');
  const [editDocumentNumber, setEditDocumentNumber] = useState<string>('');
  const [editNotes, setEditNotes] = useState<string>('');
  const [isUpdatingType, setIsUpdatingType] = useState(false);

  const parseDocumentDetails = useCallback((doc: any): Record<string, unknown> => {
    const raw = doc?.details;
    if (!raw) return {};
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
      } catch {
        return {};
      }
    }
    if (typeof raw === 'object' && !Array.isArray(raw)) return { ...raw };
    return {};
  }, []);

  const handleEditDocumentType = useCallback((doc: any) => {
    if (!canEdit) return;
    const details = parseDocumentDetails(doc);
    setDocumentToEdit(doc);
    setEditDocumentType(doc?.type || '');
    setEditDocumentNumber(
      details.documentNumber != null ? String(details.documentNumber) : ''
    );
    setEditNotes(details.notes != null ? String(details.notes) : '');
    setIsEditTypeModalOpen(true);
  }, [canEdit, parseDocumentDetails]);

  const handleCloseEditTypeModal = useCallback(() => {
    setIsEditTypeModalOpen(false);
    setDocumentToEdit(null);
    setEditDocumentType('');
    setEditDocumentNumber('');
    setEditNotes('');
  }, []);

  const handleSaveDocumentType = useCallback(async () => {
    if (!documentToEdit?.docId || !editDocumentType?.trim()) {
      Notification.warning('Please select a document type');
      return;
    }

    setIsUpdatingType(true);
    try {
      const existingDetails = parseDocumentDetails(documentToEdit);
      await axiosConfig.put(
        `/arkRegistryDocuments/updateArkRegistryDocument/${documentToEdit.docId}`,
        {
          projectId,
          type: editDocumentType.trim(),
          details: {
            ...existingDetails,
            documentNumber: editDocumentNumber.trim() || null,
            notes: editNotes.trim() || null,
          },
        }
      );
      Notification.success('Document updated successfully');
      handleCloseEditTypeModal();
      dispatch(fetchARKRegistryDocs({ projectId }));
    } catch (error: any) {
      Notification.error(
        'Failed to update document',
        error?.response?.data?.message || error?.message || 'Unknown error'
      );
    } finally {
      setIsUpdatingType(false);
    }
  }, [
    documentToEdit,
    editDocumentType,
    editDocumentNumber,
    editNotes,
    projectId,
    dispatch,
    handleCloseEditTypeModal,
    parseDocumentDetails,
  ]);

  // State for file upload
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedTags, setSelectedTags] = useState<{ [fileIndex: number]: string[] }>({});
  const [isUploading, setIsUploading] = useState(false);
  // Document type for upload: ARC Strike Report or TQ's
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>('ARC_STRIKE_DOCUMENT');
  const [isAddingDocumentType, setIsAddingDocumentType] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [documentNumber, setDocumentNumber] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // Handle file selection from DocumentUpload component
  const handleFileSelect = useCallback((files: FileList | null) => {
    if (files && files.length > 0) {
      setSelectedFiles(Array.from(files));
    } else {
      setSelectedFiles([]);
    }
  }, []);

  // Handle tags change
  const handleTagsChange = useCallback((fileIndex: number, tags: string[]) => {
    setSelectedTags(prev => ({
      ...prev,
      [fileIndex]: tags
    }));
  }, []);

  // Handle upload
  const handleUpload = useCallback(async () => {
    if (selectedFiles.length === 0) return;
    
    
    setIsUploading(true);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const fileTags = selectedTags[i] || [];
        
        // 1. Get presigned URL
        const presignedResponse = await axiosConfig.post('/arkRegistryDocuments/getSimplePreSignedUrl', {
          projectId,
          fileName: file.name,
          contentType: file.type || 'application/octet-stream'
        });
        
        // Access nested data structure
        const responseData = presignedResponse.data?.data;
        if (!responseData?.presignedUrl) {
          throw new Error('Failed to get presigned URL');
        }
        
        const { presignedUrl, s3Key } = responseData;
        
        // 2. Upload file directly to S3
        await fetch(presignedUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' }
        });
        
        // 3. Create document record with tags, document type, document number, and notes
        await axiosConfig.post('/arkRegistryDocuments/createUpdateArkRegistryDocument', {
          projectId,
          type: selectedDocumentType,
          fileName: file.name,
          s3Key,
          uploadDate: new Date().toISOString(),
          uploadedBy: user.email || 'User',
          tags: fileTags.map((tag: string) => Number(tag)),
           details: {
            documentNumber: documentNumber?.trim() || null,
            notes: notes?.trim() || null
           },
        });
      }
      
      Notification.success('Documents uploaded successfully');
      
      // Force refresh after a short delay to ensure backend has processed
      setTimeout(() => {
      dispatch(fetchARKRegistryDocs({ projectId }));
      }, 500);
      
      setIsUploadModalOpen(false);
      setSelectedFiles([]);
      setSelectedTags({});
      setDocumentNumber('');
      setNotes('');
    } catch (error: any) {
      Notification.error('Failed to upload documents', error?.message || 'Unknown error');
    } finally {
      setIsUploading(false);
    }
  }, [selectedFiles, selectedTags, selectedDocumentType, documentNumber, notes, projectId, dispatch]);

  // Close upload modal
  const handleCloseUploadModal = useCallback(() => {
    setIsUploadModalOpen(false);
    setSelectedFiles([]);
    setSelectedTags({});
  }, []);

  // Load document types on mount
  useEffect(() => {
    const loadDocumentTypes = async () => {
      try {
        const tags = await getListMaterialCategory('ARK_REGISTRY_DOCUMENT_TYPE');
        if (tags && tags.length > 0) {
          const docTypeTags = tags.filter((tag: any) => 
            tag.tags_type === 'ARK_REGISTRY_DOCUMENT_TYPE' ||
            tag.tags_type === 'DOCUMENT_TYPE' ||
            tag.tags_type?.toLowerCase().includes('document_type')
          );
          setDocumentTypes(docTypeTags);
        }
      } catch (error) {
        console.error('Failed to load document types:', error);
      }
    };

    if (projectId) {
      loadDocumentTypes();
    }
  }, [projectId]);

  // Create document type options
  const documentTypeOptions = useMemo(() => {
    // Convert database tags to SearchableSelect format (use name as value — stored as specification/type)
    const dbOptions = documentTypes.map((tag: any) => {
      const name = tag.tags_name || tag.name || '';
      return {
        id: name,
        name,
      };
    }).filter(opt => opt.id && opt.name);

    return dbOptions.length > 0 ? dbOptions : [];
  }, [documentTypes]);

  // Handle adding new document type
  const handleAddDocumentType = async (docTypeName: string) => {
    if (docTypeName.trim()) {
      // Check if it already exists
      if (documentTypeOptions.find((opt: any) => opt.name.toLowerCase() === docTypeName.trim().toLowerCase())) {
        Notification.warning(`${docTypeName.trim()} document type already exists`);
        return;
      }

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const apiPayload = {
        tags_name: docTypeName.trim(),
        project_id: projectId,
        tags_type: 'ARK_REGISTRY_DOCUMENT_TYPE',
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload, 'Document Type');

      if (res) {
        // Reload document types from database
        try {
          const tags = await getListMaterialCategory();
          if (tags && tags.length > 0) {
            const docTypeTags = tags.filter((tag: any) => 
              tag.tags_type === 'ARK_REGISTRY_DOCUMENT_TYPE' ||
              tag.tags_type === 'DOCUMENT_TYPE' ||
              tag.tags_type?.toLowerCase().includes('document_type')
            );
            setDocumentTypes(docTypeTags);
          }
        } catch (error) {
          console.error('Failed to reload document types:', error);
          // Fallback: add to current list
          const updatedTypes = [...documentTypes, res];
          setDocumentTypes(updatedTypes);
        }
        setIsAddingDocumentType(false);
        // Set the newly added document type as selected
        setSelectedDocumentType(docTypeName.trim());
        Notification.success('Document type added successfully');
      }
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Get file type from extension
  const getFileType = (fileName: string) => {
    if (!fileName) return '-';
    const ext = fileName.split('.').pop()?.toLowerCase();
    const typeMap: Record<string, string> = {
      'pdf': 'PDF',
      'doc': 'Word',
      'docx': 'Word',
      'xls': 'Excel',
      'xlsx': 'Excel',
      'csv': 'CSV',
      'jpg': 'Image',
      'jpeg': 'Image',
      'png': 'Image'
    };
    return typeMap[ext || ''] || ext?.toUpperCase() || '-';
  };

  // Document data
  const documents = useMemo(() => {
    // Handle both array and object with documents property
    if (Array.isArray(arkRegistryDocs)) {
      return arkRegistryDocs;
    }
    if (arkRegistryDocs && typeof arkRegistryDocs === 'object' && arkRegistryDocs.documents) {
      return Array.isArray(arkRegistryDocs.documents) ? arkRegistryDocs.documents : [];
    }
    return [];
  }, [arkRegistryDocs]);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>([]);

  const docTypeOptions = useMemo(() => {
    const unique = new Set<string>();
    documents.forEach((d: any) => { if (d.type) unique.add(d.type); });
    return Array.from(unique).sort().map((t) => ({ value: t, label: t }));
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return documents.filter((d: any) => {
      if (selectedDocTypes.length > 0 && !selectedDocTypes.includes(d.type)) return false;
      if (!q) return true;
      const haystacks = [
        d.fileName, d.type, d.details?.documentNumber, d.details?.notes, d.uploadedBy,
      ].filter(Boolean).map((x: any) => String(x).toLowerCase());
      return haystacks.some((h) => h.includes(q));
    });
  }, [documents, searchQuery, selectedDocTypes]);

  const activeFilterCount = selectedDocTypes.length;
  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedDocTypes([]);
  };

  // // Debug logging
  // useEffect(() => {
  //   console.log('ARK Registry Docs State:', {
  //     arkRegistryDocs,
  //     documents,
  //     documentsLength: documents.length,
  //     loading: arkRegistryDocsLoading,
  //     error: arkRegistryDocsError
  //   });
  // }, [arkRegistryDocs, documents, arkRegistryDocsLoading, arkRegistryDocsError]);

  // If viewer is open, render only the viewer
  if (viewerFile) {
    return (
      <PdfViewer
        fileUrl={viewerFile.fileUrl}
        fileName={viewerFile.fileName}
        onClose={closeViewer}
      />
    );
  }

  return (
    <div className="min-w-0 space-y-6">
      <Card className="min-w-0 border-none shadow-none">
        <CardContent className="p-4 sm:p-6">
          <div className="mb-6 flex w-full flex-wrap items-center justify-end gap-2">
            {canEdit && (
              <Button
                onClick={() => setIsUploadModalOpen(true)}
                size="sm"
                className="flex items-center justify-center gap-2 bg-hero-red text-white hover:bg-hero-red-60"
              >
                <Upload className="h-4 w-4" />
                Upload Document
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E2E5EA] bg-white transition-colors hover:bg-page-bg">
                <MoreHorizontal className="h-4 w-4 text-text-grey" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleRefresh}>
                  <RefreshCw className={`mr-2 h-4 w-4 ${arkRegistryDocsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Filter + Table (Material Traceability pattern) */}
          <div className="min-w-0 space-y-4">
            <CollapsibleFilterBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search by file name, type, number, comments, or uploader..."
              activeFilterCount={activeFilterCount}
              filteredCount={filteredDocuments.length}
              filteredCountLabel="documents"
              onResetFilters={handleResetFilters}
            >
              <MultiSelect
                options={docTypeOptions}
                selected={selectedDocTypes}
                onChange={setSelectedDocTypes}
                placeholder="Document Type"
                isSearchable
              />
            </CollapsibleFilterBar>

            {arkRegistryDocsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-5 w-5 animate-spin text-heritage-grey mr-2" />
                <span className="text-[13px] text-heritage-grey">Loading documents...</span>
              </div>
            ) : arkRegistryDocsError ? (
              <div className="flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
                <AlertCircle className="h-5 w-5 text-hero-red" />
                <span className="text-[13px] text-hero-red">Error loading documents: {arkRegistryDocsError}</span>
              </div>
            ) : documents.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 bg-white border border-[#E2E5EA] rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-page-bg flex items-center justify-center mb-4">
                  <FileText className="h-6 w-6 text-heritage-grey" />
                </div>
                <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue mb-1">
                  No Documents Yet
                </h3>
                <p className="text-[12px] text-heritage-grey max-w-xs text-center">
                  {canEdit
                    ? <>No documents uploaded yet. Click <span className="font-semibold text-text-grey">Upload Document</span> to add files.</>
                    : 'No documents uploaded yet.'}
                </p>
              </div>
            ) : (
              <Card className="min-w-0">
                <CardContent className="p-3 sm:p-4">
                  <div className="min-w-0 overflow-x-auto">
                    <div className="overflow-y-auto max-h-[400px] md:max-h-[500px] lg:max-h-[calc(100vh-350px)]">
                      <Table containerClassName="">
                        <TableHeader className="sticky top-0 z-20 bg-white">
                          <TableRow>
                            <TableHead className="sticky left-0 z-20 bg-white text-center w-[60px]">
                              <span className="inline-flex items-center gap-1.5"><Hash className="h-3 w-3" />Sno</span>
                            </TableHead>
                            <TableHead>File Name</TableHead>
                            <TableHead>Doc Type</TableHead>
                            <TableHead>Document Number</TableHead>
                            <TableHead>Comments</TableHead>
                            <TableHead>Upload Date</TableHead>
                            <TableHead>Uploaded By</TableHead>
                            <TableHead className="text-center w-[100px] sticky right-0 z-10 bg-white">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredDocuments.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} className="text-center text-[13px] text-heritage-grey py-8">
                                No documents match the current filters.
                              </TableCell>
                            </TableRow>
                          ) : filteredDocuments.map((doc: any, index: number) => (
                            <TableRow key={doc.docId || index}>
                              <TableCell className="sticky left-0 z-10 bg-white text-center">{index + 1}</TableCell>
                              <TableCell>{doc.fileName || '-'}</TableCell>
                              <TableCell>{doc.type || '-'}</TableCell>
                              <TableCell>{doc.details?.documentNumber || '-'}</TableCell>
                              <TableCell>
                                <div className="max-w-[200px] truncate" title={doc.details?.notes || ''}>
                                  {doc.details?.notes || '-'}
                                </div>
                              </TableCell>
                              <TableCell>{formatDate(doc.uploadDate || doc.createdAt)}</TableCell>
                              <TableCell>{doc.uploadedBy || '-'}</TableCell>
                              <TableCell className="text-center sticky right-0 z-10 bg-white">
                                <div className="flex items-center justify-center gap-2">

                                <Button
                                    variant="outline"
                                    className="text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                                    title={`Edit ${doc.fileName}`}
                                    onClick={() => handleEditDocumentType(doc)}
                                    disabled={!canEdit}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    className="text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                                    title={`Download ${doc.fileName}`}
                                    onClick={() => handleDownloadDocument(doc)}
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                  {canEdit && (
                                    <Button
                                      variant="outline"
                                      className="text-hero-red hover:text-hero-red hover:bg-[rgba(235,28,55,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                                      title={`Delete ${doc.fileName}`}
                                      onClick={() => handleDeleteClick(doc)}
                                      disabled={deleteARKRegistryDocLoading}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Upload Document Modal */}
      <Dialog open={isUploadModalOpen} onOpenChange={handleCloseUploadModal}>
        <DialogContent className="w-[95vw] max-w-[1200px] max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0 pr-14">
            <div className="flex items-center justify-between gap-4">
              <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
                Upload Document
              </DialogTitle>
              <Button
                onClick={handleUpload}
                disabled={selectedFiles.length === 0 || isUploading}
                className="flex items-center gap-2 bg-hero-red hover:bg-hero-red-60 text-white"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Upload
                  </>
                )}
              </Button>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-2 overflow-x-hidden">
            <div className="py-4 space-y-4">


            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Document Type */}
              <div className="space-y-2">
                <Label htmlFor="documentType" className="text-[12px] font-medium text-text-grey">Document Type</Label>
                <div className="relative mt-1">
                  <SearchableSelect
                    value={selectedDocumentType}
                    onValueChange={(value: string) => {
                      setSelectedDocumentType(value);
                    }}
                    placeholder="Select document type"
                    searchPlaceholder="Search document types..."
                    options={documentTypeOptions}
                    showAddButton={true}
                    onAddNew={() => setIsAddingDocumentType(true)}
                    addNewText="Add New Document Type"
                  />
                </div>
              </div>

              {/* Document Number */}
              <div className="space-y-2">
                <Label htmlFor="documentNumber" className="text-[12px] font-medium text-text-grey">Document Number</Label>
                <Input
                  id="documentNumber"
                  value={documentNumber}
                  onChange={(e) => setDocumentNumber(e.target.value)}
                  placeholder="Enter document number"
                  className="w-full"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-[12px] font-medium text-text-grey">Notes</Label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-3 border border-[#E2E5EA] rounded-lg text-[13px] text-text-grey min-h-[80px] resize-none focus:outline-none focus:ring-1 focus:ring-hero-red focus:border-hero-red"
                placeholder="Enter notes..."
              />
            </div>

              <DocumentUpload
                onUpload={handleFileSelect}
                acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls,.jpg,.jpeg,.png"
                multiple={true}
                maxFiles={100}
                buttonText="Select Document"
                label="Document"
                description="Upload documents (PDF, Excel, DOC, etc...)"
                required={true}
                className="w-full"
                enableNDTValidation={false}
                enableTagging={true}
                specificationType={selectedDocumentType}
                projectId={projectId}
                onTagsChange={handleTagsChange}
                selectedTags={selectedTags}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add New Document Type Dialog */}
      <AddNewItemDialog
        open={isAddingDocumentType}
        onOpenChange={setIsAddingDocumentType}
        title="Add New Document Type"
        label="Document Type Name"
        placeholder="Enter document type (e.g., ARC_STRIKE_DOCUMENT, TQ's)"
        onAdd={handleAddDocumentType}
        buttonText="Add Document Type"
        cancelText="Cancel"
      />

      {/* Edit Document Dialog */}
      <Dialog open={isEditTypeModalOpen} onOpenChange={(open) => !open && handleCloseEditTypeModal()}>
        <DialogContent className="w-[95vw] max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue">
              Edit Document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {documentToEdit?.fileName && (
              <div className="text-[13px] text-text-grey">
                File: <span className="font-medium">{documentToEdit.fileName}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey">Document Type</Label>
              <SearchableSelect
                value={editDocumentType}
                onValueChange={(value: string) => setEditDocumentType(value)}
                placeholder="Select document type"
                searchPlaceholder="Search document types..."
                options={documentTypeOptions}
                showAddButton={true}
                onAddNew={() => setIsAddingDocumentType(true)}
                addNewText="Add New Document Type"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editDocumentNumber" className="text-[12px] font-medium text-text-grey">
                Document Number
              </Label>
              <Input
                id="editDocumentNumber"
                value={editDocumentNumber}
                onChange={(e) => setEditDocumentNumber(e.target.value)}
                placeholder="Enter document number"
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editNotes" className="text-[12px] font-medium text-text-grey">
                Notes
              </Label>
              <textarea
                id="editNotes"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                className="w-full p-3 border border-[#E2E5EA] rounded-lg text-[13px] text-text-grey min-h-[80px] resize-none focus:outline-none focus:ring-1 focus:ring-hero-red focus:border-hero-red"
                placeholder="Enter notes..."
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCloseEditTypeModal} disabled={isUpdatingType}>
              Cancel
            </Button>
            <Button
              className="bg-hero-red text-white hover:bg-hero-red-60"
              onClick={handleSaveDocumentType}
              disabled={isUpdatingType || !editDocumentType?.trim()}
            >
              {isUpdatingType ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {documentToDelete && (
        <DeleteConfirmationDialog
          isOpen={isDeleteModalOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          isLoading={deleteARKRegistryDocLoading}
          title="Delete ARC Strike Report Document"
          itemType="ARC Strike Report Document"
          itemName={documentToDelete?.fileName || 'Document'}
          warningMessage="This document will be permanently removed from the system. This action cannot be undone."
          detailsRenderer={(item: any) => (
            <div className="space-y-2 bg-page-bg border border-[#E2E5EA] p-3 rounded-md">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-heritage-grey" />
                <span className="text-[13px] font-medium text-text-grey">{item.fileName || 'Unknown file'}</span>
              </div>
              {item.type && (
                <div className="text-[12px] text-heritage-grey">
                  Type: {item.type}
                </div>
              )}
              {(item.uploadDate || item.createdAt) && (
                <div className="text-[12px] text-heritage-grey">
                  Uploaded: {formatDate(item.uploadDate || item.createdAt)}
                </div>
              )}
            </div>
          )}
          itemToDelete={documentToDelete}
        />
      )}
    </div>
  );
};

